import { Box, Typography, IconButton, List, ListItem, ListItemText } from '@mui/material';
import {
  useLocale,
  useResourceCreate,
  useFileUpload,
  useResourceShow,
  useResourceUpdate,
  useResourceDelete
} from '@koopajs/react';
import { RenderDocumentIcon } from 'components/RenderDocumentIcon';
import prettyBytes from 'pretty-bytes';
import { FormControllerTemp } from 'components/temp/FormControllerTemp';
import { ICommittee, IDocument, IUploadedMeeting } from 'types';
import { useState, useCallback } from 'react';
import { Edit as EditIcon, CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import { usePermissions, useAppContext } from '@koopajs/react';
import { formatDate } from 'utils/DateTime/formatDate';
import { useCommitteeName } from 'components/hooks/useCommitteeName';
import { DateTime } from 'luxon';
import { ErrorMessageTemp } from 'components/temp/ErrorMessageTemp';
import { DocumentList } from 'components/DocumentList';
import { useAppSnackbar } from 'components/hooks/useAppSnackbar';
import { UploadedMeetingCardEditable } from './UploadedMeetingAddCardEditable';

export interface IFile {
  id?: string;
  blobUrl?: string;
  file: File;
}

interface IUploadedMeetingAddCardProps {
  file: IFile;
  committees: ICommittee[];
  index: number;
}

export const UploadedMeetingAddCard: React.FC<IUploadedMeetingAddCardProps> = (props) => {
  const {
    file: { file, blobUrl, id: fileId },
    committees,
    index
  } = props;
  const { t, locale } = useLocale();

  const titleWithoutFileFormat = file.name ? file?.name.replace(/\.[^/.]+$/, '') : undefined;

  const keyPrefix = 'UploadedMeetings.UploadedMeetingCard';

  const { getComponentContext, setComponentContext } = useAppContext();
  const { files } = getComponentContext('uploadedMeetings') as { files: IFile[] };

  const [isInEditMode, setIsInEditMode] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);

  const isUserWorkspaceOwner = usePermissions({ requiredPermissions: 'owner' });

  const { triggerSnackbar } = useAppSnackbar();

  const fileUpload = useFileUpload();

  const { resource: uploadedMeeting } = useResourceShow<IUploadedMeeting>({
    path: '/uploaded-meetings',
    id: fileId
  });

  const { resource: mainDocument } = useResourceShow<IDocument>({
    path: '/documents',
    id: uploadedMeeting?.mainDocumentId
  });

  const { createResource: createUploadedMeeting, errorMessage } = useResourceCreate({
    path: '/uploaded-meetings'
  });

  const {
    updateResource: updateUploadedMeeting,
    errorMessage: errorMessageUpdate,
    isProcessing: isUpdateUploadedMeetingProcessing
  } = useResourceUpdate({
    path: '/uploaded-meetings',
    id: uploadedMeeting?.id as string
  });

  const {
    deleteResource: deleteUploadedMeeting,
    errorMessage: errorMessageDelete,
    isProcessing: isDeleteUploadedMeetingProcessing
  } = useResourceDelete({
    path: '/uploaded-meetings',
    id: uploadedMeeting?.id as string
  });

  const committeeName = useCommitteeName(uploadedMeeting?.committeeId as string);
  const isPdfOrImage =
    mainDocument?.attachedFile?.format === 'application/pdf' ||
    mainDocument?.attachedFile?.format.startsWith('image');

  const handleSetToEditMode = useCallback(() => {
    return () => setIsInEditMode(true);
  }, []);

  // SAVE UPLOADED MEETING
  const handleSubmitCreate = useCallback(
    async (data: object) => {
      const typedData = data as {
        committeeId: string;
        title: string;
        startDateTime: string;
      };

      setIsProcessing(true);

      const fileUri = await fileUpload.upload(file);

      const payload = {
        ...typedData,
        startDateTime: typedData.startDateTime
          ? new Date(typedData.startDateTime + `T00:00:00Z`).toISOString()
          : '',
        attachedFile: {
          uri: fileUri,
          format: file.type,
          name: file.name,
          sizeBytes: file.size
        }
      };

      let uploadedDocumentId = '';
      const isSuccess = await createUploadedMeeting(payload, (id) => {
        uploadedDocumentId = id;
      });
      setIsProcessing(false);

      if (isSuccess) {
        setIsInEditMode(false);

        const updatedFiles = files.map((f, i) => {
          if (f.file.lastModified === file.lastModified && f.file.name === file.name && i === index) {
            const updatedFile = Object.assign(f, { id: uploadedDocumentId });

            return updatedFile;
          } else {
            return f;
          }
        });

        setComponentContext('uploadedMeetings', { files: updatedFiles });
      }

      return isSuccess;
    },
    [file, files]
  );

  // UPDATE UPLOADED MEETING
  const handleSubmitUpdate = useCallback(
    async (data: object) => {
      const typedData = data as {
        committeeId: string;
        title: string;
        startDateTime: string;
      };

      const payload = {
        ...typedData,
        startDateTime: typedData.startDateTime
          ? new Date(typedData.startDateTime + `T00:00:00Z`).toISOString()
          : ''
      };

      const isSuccess = await updateUploadedMeeting(payload);

      if (isSuccess) {
        setIsInEditMode(false);
      }

      return isSuccess;
    },
    [uploadedMeeting?.id]
  );

  // DELETE UPLOADED MEETING
  const handleDeleteUploadedMeeting = useCallback(async () => {
    if (uploadedMeeting?.id) {
      const isSuccess = await deleteUploadedMeeting();
      if (isSuccess) {
        const updatedFiles = files.filter((f, i) => f.id !== uploadedMeeting?.id);

        setComponentContext('uploadedMeetings', { files: updatedFiles });

        triggerSnackbar({
          snackbarText: t(keyPrefix + '.snackbarDeleteSuccess'),
          variant: 'success'
        });
      } else {
        triggerSnackbar({
          snackbarText: t(keyPrefix + '.snackbarDeleteFail'),
          variant: 'error'
        });
      }
    } else {
      const updatedFiles = files.filter((f, i) => i !== index && f.file.name !== file.name);
      setComponentContext('uploadedMeetings', { files: updatedFiles });

      triggerSnackbar({
        snackbarText: t(keyPrefix + '.snackbarDeleteSuccess'),
        variant: 'success'
      });
    }
  }, [uploadedMeeting?.id, JSON.stringify(files)]);

  return (
    <Box
      sx={{
        backgroundColor: '#FFFFFF',
        padding: '24px',
        borderRadius: '4px',
        my: 2,
        border: '1px solid #dddddd'
      }}
      data-cy="uploaded-meeting-add-card"
    >
      <Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 3
          }}
        >
          {/* PDF VIEWER */}

          {(isPdfOrImage || blobUrl) && (
            <Box sx={{ width: { xs: '100%', md: '40%' } }}>
              <iframe
                style={{
                  border: '1px solid gray',
                  backgroundColor: 'rgba(0, 0, 0, 0.11)',
                  width: '100%',
                  height: '100%'
                }}
                className="rr-block"
                src={mainDocument?.attachedFileUri || blobUrl}
              />
            </Box>
          )}

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              width: { xs: '100%', md: isPdfOrImage || blobUrl ? '60%' : '100%' },
              gap: 3
            }}
          >
            <ErrorMessageTemp error={errorMessage || errorMessageUpdate || errorMessageDelete} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  minWidth: '0px'
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    overflow: 'hidden'
                  }}
                >
                  {(mainDocument?.attachedFile?.format || file?.type) && (
                    <RenderDocumentIcon
                      fileType={mainDocument?.attachedFile?.format || file.type}
                      sx={{ mr: 1 }}
                    />
                  )}
                  <Typography
                    variant="body2"
                    display="inline"
                    sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                    data-cy="uploaded-meeting-add-card_file-name"
                    className="rr-mask"
                  >
                    {mainDocument?.attachedFile?.name || file?.name}
                  </Typography>
                </Box>

                <span>&#183;</span>
                <Typography variant="body2" sx={{ opacity: 0.6, flex: 'none' }}>
                  {(mainDocument?.attachedFile?.sizeBytes || file?.size) &&
                    prettyBytes(mainDocument?.attachedFile.sizeBytes || file.size)}
                </Typography>
              </Box>
              {!isInEditMode && isUserWorkspaceOwner && (
                <IconButton
                  data-cy="uploaded-meeting-add-card_edit-button"
                  sx={{ alignSelf: 'flex-start', ml: 2 }}
                  onClick={handleSetToEditMode()}
                >
                  <EditIcon />
                </IconButton>
              )}
            </Box>
            {isInEditMode && isUserWorkspaceOwner ? (
              <FormControllerTemp
                onSubmit={uploadedMeeting?.id ? handleSubmitUpdate : handleSubmitCreate}
                defaultValues={{
                  title: uploadedMeeting?.title || titleWithoutFileFormat,
                  startDateTime:
                    uploadedMeeting?.startDateTime &&
                    DateTime.fromISO(uploadedMeeting?.startDateTime, { zone: 'utc' }).toFormat('yyyy-MM-dd'),
                  committeeId: uploadedMeeting?.committeeId
                }}
              >
                <UploadedMeetingCardEditable
                  committees={committees}
                  uploadedMeetingId={uploadedMeeting?.id}
                  handleDeleteUploadedMeeting={handleDeleteUploadedMeeting}
                  isProcessing={isProcessing || isUpdateUploadedMeetingProcessing}
                />
              </FormControllerTemp>
            ) : (
              <>
                <List>
                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' } }}>
                    <ListItem
                      alignItems="flex-start"
                      sx={{ px: 0 }}
                      data-cy="uploaded-meeting-add-card_date-li"
                    >
                      <ListItemText
                        primary={t(keyPrefix + '.meetingDate')}
                        primaryTypographyProps={{ variant: 'caption', sx: { opacity: 0.6 } }}
                        secondary={
                          <Typography
                            sx={{
                              display: 'inline',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              minWidth: '0px'
                            }}
                            component="span"
                            variant="body1"
                            color="text.primary"
                          >
                            {uploadedMeeting &&
                              formatDate({
                                isoString: uploadedMeeting?.startDateTime as string,
                                locale,
                                format: 'DATE_FULL',
                                isUTC: true
                              })}
                          </Typography>
                        }
                        sx={{ px: 0 }}
                      />
                    </ListItem>

                    <ListItem
                      alignItems="flex-start"
                      sx={{ px: 0 }}
                      data-cy="uploaded-meeting-add-card_committee-li"
                    >
                      <ListItemText
                        primary={t(keyPrefix + '.meetingType')}
                        primaryTypographyProps={{ variant: 'caption', sx: { opacity: 0.6 } }}
                        secondary={
                          <Typography
                            sx={{
                              display: 'inline',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              minWidth: '0px'
                            }}
                            component="span"
                            variant="body1"
                            color="text.primary"
                          >
                            {uploadedMeeting && committeeName}
                          </Typography>
                        }
                        sx={{ px: 0 }}
                      />
                    </ListItem>
                  </Box>
                  <ListItem
                    alignItems="flex-start"
                    sx={{ px: 0 }}
                    data-cy="uploaded-meeting-add-card_title-li"
                  >
                    <ListItemText
                      primary={t(keyPrefix + '.title')}
                      primaryTypographyProps={{ variant: 'caption', sx: { opacity: 0.6 } }}
                      secondary={
                        <Typography
                          sx={{
                            display: 'inline',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            maxWidth: '100%',
                            minWidth: '0px',
                            whiteSpace: 'nowrap'
                          }}
                          component="span"
                          variant="body1"
                          color="text.primary"
                        >
                          {uploadedMeeting && uploadedMeeting.title}
                        </Typography>
                      }
                      sx={{ px: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    />
                  </ListItem>
                </List>
              </>
            )}
            {uploadedMeeting?.id && !isInEditMode && (
              <Box
                sx={{
                  display: 'flex',
                  gap: { xs: 2, md: 0 },
                  flexDirection: { xs: 'column', md: 'row' },
                  justifyContent: {
                    xs: 'flex-start',
                    md: 'space-between'
                  }
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <DocumentList
                    path={`/uploaded-meetings/${uploadedMeeting?.id}/documents`}
                    isAccordion={true}
                    accordionOptions={{ documentsVisible: 2 }}
                    isEditable={isUserWorkspaceOwner && Boolean(uploadedMeeting?.id)}
                    sxDocument={{ display: 'flex' }}
                  />
                </Box>

                <Box
                  sx={{ display: 'flex', py: '6px', alignSelf: { md: 'flex-end' }, minWidth: 'fit-content' }}
                >
                  <CheckCircleIcon color="success" />
                  <Typography variant="body1" sx={{ opacity: '0.6', ml: 1 }}>
                    {t(keyPrefix + '.addedToBooks')}
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
